import { useState } from 'react';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid2';
import { styled } from '@mui/material/styles';
import NavigateNextRoundedIcon from '@mui/icons-material/NavigateNextRounded';
import { CulturalPost } from '../../../types';
import { formatDate } from '../../../utils';

const TitleTypography = styled(Typography)(({ theme }) => ({
  position: 'relative',
  textDecoration: 'none',
  fontFamily:
    '"Playfair Display", "Georgia", "Times New Roman", serif !important',
  '&:hover': { cursor: 'pointer', color: theme.palette.primary.main },
  '& .arrow': {
    visibility: 'hidden',
    position: 'absolute',
    right: 0,
    top: '50%',
    transform: 'translateY(-50%)',
  },
  '&:hover .arrow': {
    visibility: 'visible',
    opacity: 0.7,
  },
  '&:focus-visible': {
    outline: '3px solid',
    outlineColor: 'hsla(210, 98%, 48%, 0.5)',
    outlineOffset: '3px',
    borderRadius: '8px',
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    width: 0,
    height: '1px',
    bottom: 0,
    left: 0,
    backgroundColor: theme.palette.text.primary,
    opacity: 0.3,
    transition: 'width 0.3s ease, opacity 0.3s ease',
  },
  '&:hover::before': {
    width: '100%',
  },
}));

type LatestProps = {
  posts: CulturalPost[];
  isNextPage?: boolean;
};

export default function Latest({ posts, isNextPage }: LatestProps) {
  const { palette } = useTheme();
  const navigate = useNavigate();
  const [focusedCardIndex, setFocusedCardIndex] = useState<number | null>(null);

  const getMediaTypeLabel = (post: CulturalPost) => {
    if (post.video_url) return 'Video';
    if (post.audio_url) return 'Audio';
    if (post.image_url) return 'Image';
    return 'Story';
  };

  const handleFocus = (index: number) => {
    setFocusedCardIndex(index);
  };

  const handleBlur = () => {
    setFocusedCardIndex(null);
  };

  return (
    <Box>
      {!isNextPage && (
        <Typography variant="h2" color="primary" gutterBottom fontWeight="bold">
          Latest
        </Typography>
      )}
      <Grid container spacing={4} columns={12} sx={{ my: 4 }}>
        {posts?.map((post, index) => (
          <Grid key={`latest_${index}`} size={{ xs: 12, sm: 6 }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                height: '100%',
                bgcolor: '#ffff',
                p: 2,
                borderRadius: '14px',
                border: `1px solid ${palette.text.disabled}`,
                boxShadow: '0 8px 22px rgba(40, 38, 28, 0.05)',
                transition: 'transform 0.25s ease, box-shadow 0.25s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 14px 30px rgba(40, 38, 28, 0.1)',
                },
              }}
            >
              <Box sx={{ mb: 1 }}>
                <Chip
                  label={getMediaTypeLabel(post)}
                  size="small"
                  color="default"
                  sx={{ fontWeight: 600 }}
                />
              </Box>
              <TitleTypography
                gutterBottom
                variant="h6"
                onFocus={() => handleFocus(index)}
                onBlur={handleBlur}
                tabIndex={index}
                className={focusedCardIndex === index ? 'Mui-focused' : ''}
                onClick={() => navigate(`/hub/${post.id}`)}
              >
                {post.title}
                <NavigateNextRoundedIcon
                  className="arrow"
                  sx={{ fontSize: '1rem' }}
                />
              </TitleTypography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  display: '-webkit-box',
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {(() => {
                  const plain = post.content
                    .replace(/<[^>]*>/g, '')
                    .replace(/[#*_`~\-|]/g, '')
                    .replace(/\n+/g, ' ')
                    .trim();
                  return plain.length > 160
                    ? plain.slice(0, 160).trimEnd() + '…'
                    : plain;
                })()}
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  gap: 2,
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  py: '16px',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: 1,
                    alignItems: 'center',
                  }}
                >
                  <Typography variant="caption">LegacyTies team</Typography>
                </Box>
                <Typography variant="caption">
                  {formatDate(post.updated_at)}
                </Typography>
              </Box>
            </Box>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}
